import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { tripWindow } from '@kraftwerk/shared';
import type { NeedLine, NeedStatus } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import { addNeedsToList, addShoppingItem, shoppingNeeds } from '../../db/shopping.js';
import { listTrips } from '../../db/shops.js';
import { formatDay } from '../../format.js';
import { useToday } from '../../hooks/useToday.js';

/**
 * Wochenplan → Einkaufsliste.
 *
 * Erst zeigen, dann übernehmen: Die App schlägt vor, der Mensch hakt ab, was
 * wirklich auf die Liste soll. Vorausgewählt ist nur, was eindeutig fehlt —
 * „prüfen" verlangt einen Blick in den Schrank, den die App nicht werfen kann.
 */
export function PlanToList({
  householdId,
  onAdded,
}: {
  householdId: string | null;
  onAdded: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const fromId = useId();
  const toId = useId();
  const todayDate = useToday();
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const [nextTrip, setNextTrip] = useState<string | null>(null);
  // Einkaufstage (18.10.2026): vom nächsten Einkauf bis zum Tag vor dem
  // übernächsten — ohne Einkaufstag eine Woche ab heute.
  const trips = useLiveQuery(
    () => (todayDate ? listTrips(householdId, todayDate, db) : undefined),
    [householdId, todayDate],
  );

  useEffect(() => {
    // Einmal setzen, sobald „heute" und die Einkaufstage bekannt sind —
    // danach gehört der Bereich dem Nutzer.
    if (!todayDate || !trips || range) return;
    const window = tripWindow(
      todayDate,
      trips.map((trip) => trip.date),
    );
    setRange({ from: window.from, to: window.to });
    setNextTrip(window.nextTrip);
  }, [todayDate, trips, range]);

  const needs = useLiveQuery(
    () => (range ? shoppingNeeds(range.from, range.to, householdId, db) : undefined),
    [range?.from, range?.to, householdId],
  );

  // Vorauswahl erst, wenn der Bedarf da ist — und nur einmal je Zeitraum, damit
  // ein Abwählen nicht beim nächsten Neuzeichnen zurückspringt.
  useEffect(() => {
    if (!needs || picked !== null) return;
    setPicked(new Set(needs.lines.filter((line) => line.status === 'buy').map((line) => line.key)));
  }, [needs, picked]);

  if (!range || !needs) {
    return <p className="text-sm text-ink-muted">{t('session.loading')}</p>;
  }

  const selected = picked ?? new Set<string>();
  const chosen = needs.lines.filter((line) => selected.has(line.key));
  const toggle = (key: string) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setPicked(next);
  };

  return (
    <section aria-labelledby="plan-heading" className="flex flex-col gap-3">
      <div>
        <h2 id="plan-heading" className="text-lg font-semibold">
          {t('shopping.plan.heading')}
        </h2>
        <p className="text-sm text-ink-muted">{t('shopping.plan.intro')}</p>
        {nextTrip ? (
          <p className="text-xs text-ink-muted">
            {t('shops.untilNextTrip', { day: formatDay(nextTrip, 'short', i18n.language) })}
          </p>
        ) : null}
      </div>

      <div className="flex gap-2">
        <label htmlFor={fromId} className="flex flex-1 flex-col gap-1 text-xs text-ink-muted">
          {t('shopping.plan.from')}
          <input
            id={fromId}
            type="date"
            value={range.from}
            onChange={(event) => {
              if (!event.target.value) return;
              setRange({ ...range, from: event.target.value });
              setPicked(null);
            }}
            className="kw-field text-ink"
          />
        </label>
        <label htmlFor={toId} className="flex flex-1 flex-col gap-1 text-xs text-ink-muted">
          {t('shopping.plan.to')}
          <input
            id={toId}
            type="date"
            value={range.to}
            min={range.from}
            onChange={(event) => {
              if (!event.target.value) return;
              setRange({ ...range, to: event.target.value });
              setPicked(null);
            }}
            className="kw-field text-ink"
          />
        </label>
      </div>

      {needs.missingRefs > 0 ? (
        <p role="status" className="text-sm text-warn">
          {t('shopping.plan.missingRefs', { count: needs.missingRefs })}
        </p>
      ) : null}

      {needs.lines.length === 0 && needs.unstructured.length === 0 ? (
        <p className="kw-tile text-sm text-ink-muted">{t('shopping.plan.empty')}</p>
      ) : null}

      {needs.lines.length > 0 ? (
        <ul className="kw-tile flex flex-col gap-1">
          {needs.lines.map((line) => (
            <NeedRow
              key={line.key}
              line={line}
              checked={selected.has(line.key)}
              onToggle={() => toggle(line.key)}
            />
          ))}
        </ul>
      ) : null}

      {needs.lines.length > 0 ? (
        <button
          type="button"
          className="kw-btn"
          disabled={chosen.length === 0}
          onClick={() => void addNeedsToList(chosen, db).then(onAdded)}
        >
          {t('shopping.plan.add', { count: chosen.length })}
        </button>
      ) : null}

      {needs.unstructured.length > 0 ? (
        <div className="kw-tile flex flex-col gap-3">
          <div>
            <h3 className="font-medium">{t('shopping.plan.unstructuredHeading')}</h3>
            <p className="text-xs text-ink-muted">{t('shopping.plan.unstructuredIntro')}</p>
          </div>
          {needs.unstructured.map((recipe) => (
            <div key={recipe.recipeId}>
              <p className="text-sm">
                {recipe.name}{' '}
                <span className="text-ink-muted">
                  · {t('shopping.plan.times', { count: recipe.dates.length })}
                </span>
              </p>
              {recipe.items.length === 0 ? (
                <p className="text-xs text-warn">{t('shopping.plan.noItems')}</p>
              ) : (
                <ul className="mt-1 flex flex-wrap gap-1">
                  {recipe.items.map((text) => (
                    <li key={text}>
                      <QuickAdd text={text} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

/** Farbe nach Bedeutung: Bernstein nur für das, was jetzt zu tun ist. */
const STATUS_CLASS: Record<NeedStatus, string> = {
  buy: 'border-accent text-accent',
  covered: 'border-good text-good',
  listed: 'text-ink-muted',
  check: 'border-warn text-warn',
};

function NeedRow({
  line,
  checked,
  onToggle,
}: {
  line: NeedLine;
  checked: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const selectable = line.status === 'buy' || line.status === 'check';

  const detail = [
    line.neededG === null
      ? t('shopping.plan.neededUnknown')
      : t('shopping.plan.needed', { grams: line.neededG }),
    line.pantryG !== null ? t('shopping.plan.inPantry', { grams: line.pantryG }) : null,
    line.pantryUnknown ? t('shopping.plan.inPantryUnknown') : null,
    line.toBuyG !== null && line.toBuyG > 0
      ? t('shopping.plan.toBuy', { grams: line.toBuyG })
      : null,
    t('shopping.plan.for', { recipes: line.usedIn.join(', ') }),
  ]
    .filter((part) => part !== null)
    .join(' · ');

  return (
    <li>
      <label
        className={`flex min-h-11 items-center gap-3 ${selectable ? 'cursor-pointer' : 'opacity-70'}`}
      >
        <input
          type="checkbox"
          checked={checked}
          disabled={!selectable}
          onChange={onToggle}
          className="size-6 shrink-0 accent-accent"
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate">{line.name}</span>
          <span className="block text-xs text-ink-muted">{detail}</span>
        </span>
        <span className={`kw-chip shrink-0 ${STATUS_CLASS[line.status]}`}>
          {t(`shopping.status.${line.status}`)}
        </span>
      </label>
    </li>
  );
}

/** Ein Bestandteil einer Zutatenzeile — ein Tipp setzt ihn, wie er dasteht, auf die Liste. */
function QuickAdd({ text }: { text: string }): React.JSX.Element {
  const { t } = useTranslation();
  const [added, setAdded] = useState(false);

  return (
    <button
      type="button"
      disabled={added}
      aria-label={t('shopping.plan.quickAdd', { text })}
      onClick={() => void addShoppingItem({ name: text }, db).then(() => setAdded(true))}
      className={`kw-chip min-h-11 px-3 text-sm ${added ? 'border-good text-good' : ''}`}
    >
      {added ? '✓ ' : '+ '}
      {text}
    </button>
  );
}
